import {
  ELECTRICITY_RATE_FLAT_MAX_CENTS,
  ELECTRICITY_RATE_TOU_MAX_CENTS,
  electricityRateMaxCents,
  rateConfirmMessage,
  rateNeedsConfirmation,
  rateToHundredthCent,
  siteElectricityCeilingMessage,
} from '@evi/shared';

/**
 * Site electricity reimbursement rates against the Site electricity
 * ceilings, split by shape (Warren, 2026-09-27): "Flat and blended rates
 * cap at 40 cents, since a flat rate above that is a typo or a lie. TOU
 * and per-hour rows cap at 70 cents, since a California peak is real and
 * EV2-A sits at 62." The TOU / per-hour cap was raised to 80 the same day
 * (Warren, 2026-09-27: "It is a typo guard, not the harm guard ... stops
 * the cap from refusing a published California tariff. Flat stays at
 * 40."); SCE TOU-D 5-8 PM summer peaks at 74.
 *
 *   60¢  a flat rate billed per kWh, above 40 with the second step (since
 *        21 October 2026, night; see below)
 *   40¢  a per_kwh tariff's base rate and tiers
 *   80¢  every TOU window (either unit); a flat rate billed per hour; every
 *        rate on a per_hour tariff
 *
 * The API refuses a rate above its cap (validateLocationReimbursement);
 * these let each editor say so before the round trip, in the unit its
 * field takes, naming the cap that applies:
 *
 *   LocationDetailPage  flat rate typed in ¢ or $, per kWh (60) or per hour
 *                       (80), plus the saved-tariff and inline-TOU modes
 *   SiteSettingsPage    the Site persona's $ field, per the Location's
 *                       stored electricity_rate_type: per kWh (60) or per
 *                       hour (80), via flatRateMaxCents
 *   LocationTouPicker   per-window rates, stored as cents (80)
 *
 * Every check takes the cap (`maxCents`) from its caller, which picks it
 * with flatRateMaxCents / TOU_WINDOW_MAX_CENTS, so no editor can fall back
 * to the wrong one. The flat rate is stored in cents to a hundredth of a
 * cent (Location.electricity_rate_cents; Warren, 21 October 2026, night:
 * "The location form stores rates to a hundredth of a cent, so 43.63 stays
 * 43.63."), as onboarding's correction and the utility table store theirs;
 * it was whole cents before, so Hawaii's 43.63 saved as 44. The ceiling
 * test runs on the UNROUNDED cents, strictly above the cap refused, so it
 * agrees with the input's native max (60 in cents, 0.60 in dollars):
 * 60.004¢ per kWh is refused although it would store as 60.
 *
 * The flat per-kWh cap is 60 with a second confirmation above 40 (Warren,
 * 21 October 2026, night: "raise the utility and reimbursement caps to 60
 * cents, with a second confirmation above 40"). A person at these screens
 * has the second step (needsRateConfirmStep, components/RateConfirmStep),
 * so flatRateMaxCents is 60 per kWh. A tariff has none, so
 * tariffHasRateOverCeiling keeps its base and tiers at 40.
 */

export type RateDisplayUnit = 'cents' | 'dollars';
export type ElectricityRateType = 'per_kwh' | 'per_hour';
/** LocationDetailPage's combined flat-rate unit select: display unit × rate type. */
export type FlatRateUnit = `${RateDisplayUnit}_${ElectricityRateType}`;

/** The cap on a Location's flat rate: 60¢ per kWh (the default; above 40 with the second step), 80¢ per hour. */
export function flatRateMaxCents(rateType: ElectricityRateType | null | undefined): number {
  return electricityRateMaxCents('flat', rateType, true);
}

/** The cap on an inline TOU window, whatever its unit: 80¢. */
export const TOU_WINDOW_MAX_CENTS = ELECTRICITY_RATE_TOU_MAX_CENTS;

/** The cap on the Site persona's $/kWh rate: 60¢, above 40 with the second step. */
export const SITE_FLAT_PER_KWH_MAX_CENTS = ELECTRICITY_RATE_FLAT_MAX_CENTS;

/**
 * Whether a save needs Warren's second step first (21 October 2026, night): the cents it stores (to a hundredth of a
 * cent), per kWh, above 40 and at most 60, and not the rate already saved in that unit (the API asks the same: a save
 * carrying the stored rate is not asked again). `saved` absent: a new rate (a new row, a correction, a review's cost).
 */
export function needsRateConfirmStep(cents: number | null | undefined, rateType: ElectricityRateType | null | undefined = 'per_kwh',
  saved?: { cents: number | null | undefined; rateType?: ElectricityRateType | null }): boolean {
  if ((rateType ?? 'per_kwh') !== 'per_kwh' || !rateNeedsConfirmation(cents)) return false;
  return !(saved && saved.cents === cents && (saved.rateType ?? 'per_kwh') === 'per_kwh');
}

/** Warren's words for the second step: "43.63 cents per kWh is above 40 cents. Confirm this rate." */
export const rateConfirmStepMessage = rateConfirmMessage;

/** 'dollars_per_hour' → { display: 'dollars', rateType: 'per_hour' }. */
export function splitFlatRateUnit(unit: FlatRateUnit): {
  display: RateDisplayUnit;
  rateType: ElectricityRateType;
} {
  return {
    display: unit.startsWith('dollars_') ? 'dollars' : 'cents',
    rateType: unit.endsWith('_per_hour') ? 'per_hour' : 'per_kwh',
  };
}

export function joinFlatRateUnit(display: RateDisplayUnit, rateType: ElectricityRateType): FlatRateUnit {
  return `${display}_${rateType}`;
}

/**
 * A number in the field's unit → cents, unrounded. ×100 in binary
 * floating point leaves noise ($0.145 → 14.499999999999998¢, which
 * Math.round stores as 14, not 15); rounding to a billionth of a cent
 * removes it and keeps every fraction a field can take (the flat
 * input steps by 0.0001 dollars = 0.01¢).
 */
export function toCents(value: number, display: RateDisplayUnit): number {
  if (display === 'cents') return value;
  return Math.round(value * 100 * 1e9) / 1e9;
}

/**
 * Typed rate text → cents, unrounded. null when blank, unparseable or
 * negative: the caller decides what that means (the flat rate stores
 * 0, the Site page refuses to save). A comma decimal ("0,17") reads
 * as a point.
 */
export function rateTextToCents(text: string, display: RateDisplayUnit): number | null {
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return toCents(n, display);
}

/**
 * The cents a typed rate stores, to a hundredth of a cent: "43.63" ¢ and "0.4363" $ both store 43.63, "0.43635" $ stores
 * 43.64 (Warren, 21 October 2026, night: "The location form stores rates to a hundredth of a cent, so 43.63 stays
 * 43.63."). Blank or invalid → 0 (no reimbursement).
 */
export function rateTextToStoredCents(text: string, display: RateDisplayUnit): number {
  const cents = rateTextToCents(text, display);
  return cents === null ? 0 : rateToHundredthCent(cents);
}

/**
 * A stored rate back into a field, as stored: cents as written ("43.63", "18"); dollars with two decimals for whole
 * cents ("0.17") and up to four otherwise ("0.4363"). Never rounded to whole cents, so a save that only renames a
 * location sends 43.63 back, not 44.
 */
export function storedCentsToRateText(cents: number, display: RateDisplayUnit): string {
  const hundredths = rateToHundredthCent(cents);
  if (display === 'cents') return String(hundredths);
  return Number.isInteger(hundredths) ? (hundredths / 100).toFixed(2) : String(Number((hundredths / 100).toFixed(4)));
}

/** A stored rate for reading: "$0.18", "$0.4363". */
export function rateDollarsLabel(cents: number): string {
  return `$${storedCentsToRateText(cents, 'dollars')}`;
}

/** A cents value strictly above `maxCents`. Exactly the cap is allowed; null/undefined = not set. */
export function isCentsOverCeiling(cents: number | null | undefined, maxCents: number): boolean {
  return typeof cents === 'number' && cents > maxCents;
}

/** Typed rate text above `maxCents`. Blank or invalid is not "over": the form handles it separately. */
export function isRateTextOverCeiling(text: string, display: RateDisplayUnit, maxCents: number): boolean {
  return isCentsOverCeiling(rateTextToCents(text, display), maxCents);
}

/** The energy rates of a Tariff, in cents (structurally @/lib/api's Tariff). */
export interface TariffEnergyRates {
  rate_type?: ElectricityRateType | null;
  rates: {
    energy_rate?: number | null;
    time_of_use_schedule?: ReadonlyArray<{ energy_rate?: number | null }> | null;
    tiered_schedule?: ReadonlyArray<{ energy_rate?: number | null }> | null;
  };
}

/** The first rate over its cap: where it is, and the cap that applied. */
export interface OverCeilingRate {
  path: string;
  maxCents: number;
}

/**
 * The first energy rate on a tariff above its cap, mirroring the API's
 * firstOverCeilingTariffRate: the base rate and every tier at 40¢ (80¢ on
 * a per_hour tariff), every TOU row at 80¢. Every schedule is checked,
 * whatever the tariff's type, since a schedule left over from an earlier
 * type is still on the doc. The API refuses binding one as a Location's
 * electricity tariff. null = within every cap. A tariff has no second
 * confirmation, so its base and tiers stay at 40¢ after Warren's ruling of
 * 21 October 2026, night (electricityRateMaxCents without `confirmable`).
 */
export function tariffHasRateOverCeiling(tariff: TariffEnergyRates): OverCeilingRate | null {
  const { rates } = tariff;
  const flatMax = electricityRateMaxCents('flat', tariff.rate_type);
  const touMax = electricityRateMaxCents('tou', tariff.rate_type);
  if (isCentsOverCeiling(rates.energy_rate, flatMax)) return { path: 'rates.energy_rate', maxCents: flatMax };
  const tou = rates.time_of_use_schedule ?? [];
  for (let i = 0; i < tou.length; i += 1) {
    if (isCentsOverCeiling(tou[i]?.energy_rate, touMax)) {
      return { path: `rates.time_of_use_schedule[${i}].energy_rate`, maxCents: touMax };
    }
  }
  const tiers = rates.tiered_schedule ?? [];
  for (let i = 0; i < tiers.length; i += 1) {
    if (isCentsOverCeiling(tiers[i]?.energy_rate, flatMax)) {
      return { path: `rates.tiered_schedule[${i}].energy_rate`, maxCents: flatMax };
    }
  }
  return null;
}

/** The cap in the field's unit, for the input's native max: 40 or 0.4, 80 or 0.8. */
export function ceilingInUnit(display: RateDisplayUnit, maxCents: number): number {
  return display === 'dollars' ? maxCents / 100 : maxCents;
}

/** The cap as the field's unit writes it: "40¢" or "$0.40". */
export function ceilingLabel(display: RateDisplayUnit, maxCents: number): string {
  return display === 'dollars'
    ? `$${(maxCents / 100).toFixed(2)}`
    : `${maxCents}¢`;
}

/** The shared refusal for the cap, worded for the field's unit: a dollars field says "enter 0.40 or less". */
export function ceilingMessageFor(display: RateDisplayUnit, maxCents: number): string {
  return siteElectricityCeilingMessage(maxCents, display);
}

/**
 * The TOU picker's flexible rate input → WHOLE cents (Warren, 2026-09-27:
 * "The TOU picker rounding to whole cents, now"). It used to store the raw
 * product, so "$0.29" became 28.999999999999996¢. Heuristics, then rounded:
 *    "20.3"   → 20¢   (bare number = cents)
 *    "$0.25"  → 25¢   (dollar prefix = dollars → cents)
 *    "0.25"   → 25¢   (leading-zero decimal = dollars; electricity rates
 *                      rarely sit below 1¢, so it is a misformatted dollar value)
 *    "25¢"    → 25¢   (cent suffix is explicit)
 *    "$1.50"  → 150¢
 * Dollars go through toCents first, so "$0.145" is 15¢, not 14.
 * Invalid, blank or negative → { valid: false, cents: 0 }.
 */
export function parseTouRateText(raw: string): { valid: boolean; cents: number } {
  if (typeof raw !== 'string') return { valid: false, cents: 0 };
  const trimmed = raw.trim();
  if (trimmed === '') return { valid: false, cents: 0 };
  const whole = (n: number, display: RateDisplayUnit): { valid: boolean; cents: number } =>
    Number.isFinite(n) && n >= 0
      ? { valid: true, cents: Math.round(toCents(n, display)) }
      : { valid: false, cents: 0 };
  if (/¢\s*$/.test(trimmed)) return whole(Number.parseFloat(trimmed.replace(/¢\s*$/, '')), 'cents');
  if (/^\$/.test(trimmed)) return whole(Number.parseFloat(trimmed.replace(/^\$/, '')), 'dollars');
  if (/^0\.[0-9]/.test(trimmed)) return whole(Number.parseFloat(trimmed), 'dollars');
  return whole(Number.parseFloat(trimmed), 'cents');
}
